import React from "react";
import axios from "axios";
import { AlertColor, Button, IconButton, InputAdornment, Link, TextField, Tooltip, useMediaQuery} from "@mui/material";
import { Add, Search } from "@mui/icons-material";
import { DataGridPro, GridCellParams, GridColumns, GridToolbarContainer } from '@mui/x-data-grid-pro';
import Title from "../../component/Title";
import { globalTheme } from "../../../common/theme";
import { useConstructor } from "../../../common/UseConstructor";
import { CustomTablePagination } from "../../component/CustomPagination";
import { BranchInfo } from "./BranchAccessories";
import { BranchSingle, BranchSingleHandle } from "./BranchSingle";
import { useAppSelector } from "../../../common/hooks";
import { RootState } from "../../../common/Store";
import { CommonProps } from "../../component/MainContents";
import { renewToken } from "../../../services/AuthService";
import { SnackBarMessage, SnackBarMessageProps } from "../../component/SnackBarMessage";
import { useNavigate } from "react-router-dom";
import { OpenMode } from "../../../common/Enums";

export function BranchList(props: CommonProps) {
	const [loading, setLoading] = React.useState(true);
	const [hasError, setHasError] = React.useState(false);

    const isDebug = useAppSelector((state: RootState) => state.menuToggle.debugMode);

	const singleModalRef = React.useRef<BranchSingleHandle>(null);

	const navigate = useNavigate();

    const style = { ...globalTheme() };

    const tinyScreen = useMediaQuery('(max-width:1366px)');
    const smallScreen = useMediaQuery('(max-width:1920px)');
    const normalScreen = useMediaQuery('(max-width:2560px)');
  
	const [gridData, setGridData] = React.useState<BranchInfo[]>([]);

    const [searchData, setSearchData] = React.useState<any>({
        pageSize: 15,
    });

    //Dialogs and snacks
    const [snackSettings, setSnackSettings] =
      React.useState<SnackBarMessageProps>({
        open: false,
        message: "",
        subMessage: "",
        severity: undefined,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });

    function showSnackBar(message: string, subMessage: string, colour?: AlertColor) {
      setSnackSettings({
        open: true,
        message: message,
        subMessage: subMessage,
        severity: colour,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });
    }

	function CustomToolbar() {
		const [inputText, setInputText] = React.useState("");
		return (
			<GridToolbarContainer style={{ display: 'flex' }}>
				<TextField className={style.ModalContentField}
					style={{ minWidth: '100px', width: '100%' }}
					label="Region / Company Name"
					value={inputText}
					onChange={(e) => setInputText(e.target.value)}
					onKeyDown={(e) => KeyPressed(e.key, inputText)}
					InputProps={{
						endAdornment: (
						  <InputAdornment position="end">
							<IconButton onClick={() => {
								SearchBranch(inputText);
							  }}><Search />
							</IconButton>
						  </InputAdornment>
						),
					  }}   
				/>
				<div style={{ minWidth: "20px" }}></div>
				<Button variant="contained" size="small" color="secondary" style={{ margin: "4px" }} startIcon={<Add />} onClick={() => NewObject()}>Add</Button>
				<div style={{ flexGrow: 1, minWidth: "10px" }}></div>
			</GridToolbarContainer>
		);
	}

	function ReloadBranches(branch: BranchInfo)
	{
		let branchList = [...gridData];
		if (gridData.find(x => x.region == branch.region))
			branchList[gridData.findIndex(x => x.region == branch.region)] = branch;
		else
			branchList.push(branch);

		setGridData(branchList);
	}

    function KeyPressed(key: string, inputText: string) {
		if(key === 'Enter'){
			SearchBranch(inputText);
		}      
	}

	useConstructor(() => Initialise())
    function Initialise() {
		renewToken();

        SearchBranch("");
    }

	function SearchBranch(inputText: string) {
		setLoading(true);

		const params = { searchString: inputText };
		axios.get<BranchInfo[]>('Api/V1/Branch/Search', {
		  params,
		  headers: {
			'Content-Encoding': 'gzip'
		  }
		})
			.then((result) => {
				result.data.forEach(i => {
				i.region = i.region;
				});
				setGridData(result.data);
				setLoading(false);
			},
				(error) => {
				setGridData([]);
				setLoading(false);
				console.log(error);
				showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
				}
			)
			.catch((error) => {
				setLoading(false);
				console.log(error);
				showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			});
	}

	//Grid columns
	const columns: GridColumns = [
		{
			field: 'region', headerName: 'Region', flex: 1, renderCell: (params: GridCellParams) => (
				<Link
					href="#"
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						EditObject(params.id.toString());
					}}
					color="inherit"
					underline="hover">
					<strong>
						{params.value}
					</strong>
				</Link>
			)
		},
		{ field: 'companyName1', headerName: 'company Name (Eng)', flex: 4, type: 'string' },
		{ field: 'companyName2', headerName: 'company Name (Other)', flex: 4, type: 'string' },
		{ field: 'address1', headerName: 'Address 1', flex: 6, type: 'string' },
		{ field: 'address2', headerName: 'Address 2', flex: 6, type: 'string' }
	];
	  
	function NewObject() {
		let branchInfo: BranchInfo = {
			region: "",
			companyName1: "",
			companyName2: "",
			address1: "",
			address2: "",
			phone: "",
			fax: "",
			quotationNo: 0,
			poNo: 0,
			samplePONo: 0,
			invoiceNo: 0,
			japanYear: "",
			invoiceMonth: 0,
			nextMonthInvoiceNo: 0,
			statementNo: 0,
			statementMonth: 0,
			printIndex: 0,
			tradingCompanyRequired: false,
			plNo: 0
		}
		singleModalRef.current?.Initialise(OpenMode.NEW, branchInfo);
	}

	function EditObject(region: string) {
		let branch = gridData.find(x => x.region == region);
		if (branch)
			singleModalRef.current?.Initialise(OpenMode.EDIT, branch);
	}

	return (
        <div className={style.PageMain}>
			<Title pageTitle="Branches" />
			<div className={style.PageContent}>
				<DataGridPro
					getRowId={(row) => row.region}
					rows={gridData}
					columns={columns}
					pageSize={searchData.pageSize}
					onPageSizeChange={(pageSize) => setSearchData({ ...searchData, pageSize: pageSize })}
					pagination
					rowsPerPageOptions={[15, 30, 50, 100]}
					loading={loading}
					components={{
						Toolbar: CustomToolbar,
						Footer: CustomTablePagination
					}}
					componentsProps={{
						footer: { rowsPerPageOptions: [15, 30, 50, 100] }
					}}
					headerHeight={tinyScreen ? 30 : (smallScreen ? 34 : 38)}
					rowHeight={tinyScreen ? 22 : (smallScreen ? 28 : 34)}
					onRowDoubleClick={(params) => EditObject(params.row.region)}
				/>
			</div>

			<BranchSingle
				ref={singleModalRef}
				onClose={(saved: boolean, branch: BranchInfo) => {
					if (saved && branch.region != "")
						ReloadBranches(branch);
				} } 
			/>

			<SnackBarMessage {...snackSettings} />

        </div>
    );
}

